import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useActionPlans, useDeleteActionPlan } from '../../utils/reactQueryHooks';
import { useCardData } from '../common/CardContext';
import DeleteConfirmModal from '../common/DeleteConfirmModal';
import '../../styles/automation.css';

/**
 * AutomationSummary - Displays a summary of all active automation plans for a sensor hub
 * Shows current values, next updates, and allows navigation to specific automation plans
 */
const AutomationSummary = ({ sensorMacAddress }) => {
  const { cardData } = useCardData();
  
  // State for delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  
  // Delete action plan mutation
  const deleteActionPlanMutation = useDeleteActionPlan();
  
  // Fetch all active action plans for the user
  const { data: actionPlansRaw = [], isLoading: actionPlansLoading } = useActionPlans({
    is_active: true
  });

  // Ensure we have arrays - REMOVED DEBUG LOGGING TO PREVENT SPAM
  const actionPlans = Array.isArray(actionPlansRaw) ? actionPlansRaw : [];
  
  // Stabilize currentSensorData to prevent constant re-renders from MQTT updates
  const currentSensorData = useMemo(() => {
    return cardData[sensorMacAddress] || {};
  }, [cardData, sensorMacAddress]);

  // Get action plans for this specific sensor
  const sensorActionPlans = useMemo(() => {
    return actionPlans.filter(actionPlan => {
      return actionPlan.sensor_mac_address === sensorMacAddress;
    });
  }, [actionPlans, sensorMacAddress]);

  const getCurrentValueForAction = (actionType, sensorData) => {
    switch (actionType) {
      case 'light':
        return `${sensorData.mostRecentLight || 'N/A'} lux`;
      case 'irrigation':
        return `${sensorData.mostRecentMoisture || 'N/A'}%`;
      case 'fan':
        return `${sensorData.mostRecentHumidity || 'N/A'}%`;
      case 'humidity':
        return `${sensorData.mostRecentHumidity || 'N/A'}%`;
      default:
        return 'N/A';
    }
  };

  // Generate automation summary from active action plans
  const automationPlans = useMemo(() => {
    return sensorActionPlans
      .map(plan => {
        const currentValue = getCurrentValueForAction(plan.actuator_type, currentSensorData);
        
        return {
          id: plan.action_plan_id,
          name: plan.action_name || plan.actuator_description,
          actionType: plan.actuator_type,
          currentValue: currentValue,
          planType: plan.plan_type || 'Automated',
          recurrence: plan.recurrence_type || 'One-time',
          description: plan.actuator_description,
          isActive: plan.is_active,
          startDate: plan.start_date,
          endDate: plan.end_date,
          actionMacAddress: plan.action_mac_address // Add for navigation
        };
      });
  }, [sensorActionPlans, currentSensorData]);

  const getActionIcon = (actionType) => {
    const icons = {
      light: 'ri-sun-line',
      irrigation: 'ri-drop-line',
      fan: 'ri-wind-line',
      humidity: 'ri-cloud-line'
    };
    return icons[actionType] || 'ri-settings-line';
  };

  const getActionColor = (actionType) => {
    const colors = {
      light: '#ffd700',
      irrigation: '#4fc3f7',
      fan: '#81c784',
      humidity: '#ba68c8'
    };
    return colors[actionType] || '#9e9e9e';
  };

  const handleNavigateToAutomation = (plan) => {
    // Navigate to the action wrapper for this plan
    console.log('Navigate to action wrapper for plan:', plan);
    
    if (plan.actionMacAddress) {
      // Navigate to the action wrapper URL
      const actionUrl = `/actions/${plan.actionMacAddress}`;
      console.log('Navigating to:', actionUrl);
      window.location.href = actionUrl;
    } else {
      console.error('No action MAC address found for plan:', plan);
      alert('Unable to navigate: Action not found');
    }
  };

  const handleDeleteClick = (e, plan) => {
    console.log('Delete button clicked for plan:', plan);
    e.stopPropagation(); // Prevent navigation
    setPlanToDelete(plan);
    setDeleteModalOpen(true);
    console.log('Modal state set to open');
  };

  const handleConfirmDelete = async () => {
    if (!planToDelete) return;
    
    try {
      await deleteActionPlanMutation.mutateAsync(planToDelete.id);
      setDeleteModalOpen(false);
      setPlanToDelete(null);
    } catch (error) {
      console.error('Failed to delete action plan:', error);
      alert('Failed to delete automation plan. Please try again.');
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setPlanToDelete(null);
  };

  const isLoading = actionPlansLoading;
  
  if (isLoading) {
    return (
      <div className="automation-summary-wrapper">
        <div className="automation-summary-header">
          <h3 className="section-title">Automation Summary</h3>
        </div>
        <div className="no-automations">
          <div className="no-automations-icon">
            <i className="ri-loader-4-line"></i>
          </div>
          <h4>Loading automation plans...</h4>
        </div>
      </div>
    );
  }

  if (automationPlans.length === 0) {
    return (
      <div className="automation-summary-wrapper">
        <div className="automation-summary-header">
          <h3 className="section-title">Automation Summary</h3>
        </div>
        <div className="no-automations">
          <div className="no-automations-icon">
            <i className="ri-time-line"></i>
          </div>
          <h4>No active automation plans yet</h4>
          <p>Create automation plans by clicking on an action in the Zone Actions tab, then navigate to Schedule/Automate.</p>
        </div>
      </div>
    );
  }

  // Debug: Uncomment for debugging
  // console.log('🔍 AUTOMATION_DEBUG - rendering with plans:', automationPlans);
  // console.log('Delete modal state:', { deleteModalOpen, planToDelete });
  
  return (
    <div className="automation-summary-wrapper">
      <div className="automation-summary-header">
        <h3 className="section-title">Automation Summary</h3>
        <span className="automation-count">{automationPlans.length} active</span>
      </div>
      
      <div className="automation-summary-list">
        {automationPlans.map((plan) => (
          <div 
            key={plan.id} 
            className="automation-summary-item"
            onClick={() => handleNavigateToAutomation(plan)}
          >
            <div className="automation-summary-icon" style={{ color: getActionColor(plan.actionType) }}>
              <i className={getActionIcon(plan.actionType)}></i>
            </div>
            
            <div className="automation-summary-content">
              <h4 className="automation-name">{plan.name}</h4>
              <div className="automation-details">
                <span className="care-plan-label">
                  <i className="ri-plant-line"></i>
                  {plan.carePlanName}
                </span>
                <span className="current-value">
                  <i className="ri-dashboard-line"></i>
                  Current: {plan.currentValue}
                </span>
                <span className="plan-type">
                  <i className="ri-time-line"></i>
                  {plan.planType === 'seasonal' ? 'Seasonal' : 'Daily'} • {plan.recurrence}
                </span>
              </div>
            </div>
            
            <div className="automation-summary-actions">
              <button 
                className="automation-delete-btn"
                onClick={(e) => handleDeleteClick(e, plan)}
                title="Delete automation plan"
              >
                <i className="ri-delete-bin-line"></i>
              </button>
              <div className="automation-summary-arrow">
                <i className="ri-arrow-right-s-line"></i>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Automation Plan"
        message={`Are you sure you want to delete the automation plan "${planToDelete?.name ?? ''}"?`}
        warningText="This action cannot be undone. The automation plan will be permanently removed."
        isLoading={deleteActionPlanMutation.isPending}
      />
    </div>
  );
};

AutomationSummary.propTypes = {
  sensorMacAddress: PropTypes.string.isRequired
};

export default AutomationSummary; 